import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { useGym } from '../../contexts/GymContext';
import UpcomingReservations from './UpcomingReservations';
import BoardGrid from './BoardGrid';
import LoadingSpinner from '../common/LoadingSpinner';
import Button from '../common/Button';
import QuickReserveModal from '../reservations/QuickReserveModal';
import ReservationSuccessModal from '../reservations/ReservationSuccessModal';
import GymSelectorModal from '../gyms/GymSelectorModal';
import GymSelectorTrigger from '../gyms/GymSelectorTrigger';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const { selectedGym } = useGym();
  const [boards, setBoards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQuickReserveModal, setShowQuickReserveModal] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [hasUpcomingReservations, setHasUpcomingReservations] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [completedReservation, setCompletedReservation] = useState(null);
  const [showGymSelector, setShowGymSelector] = useState(false);

  const greetings = [
    'Welcome back',
    'Crush it',
    'Send it',
    'Brush that board',
    'Ready to climb',
    'Time to project',
    'C\'mon'
  ];

  // Pick a random greeting (consistent per session)
  const [greeting] = useState(() => greetings[Math.floor(Math.random() * greetings.length)]);

  useEffect(() => {
    // Only load dashboard data when we have a selected gym
    if (selectedGym) {
      loadDashboardData();

      // Auto-refresh boards every 60 seconds
      const pollInterval = setInterval(() => {
        loadDashboardData();
      }, 60000); // 60 seconds

      // Cleanup interval on unmount
      return () => clearInterval(pollInterval);
    }
  }, [selectedGym]); // Reload when selected gym changes

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('Loading dashboard data for gym:', selectedGym);

      if (!selectedGym || !selectedGym.gymId) {
        throw new Error('No gym selected');
      }

      // Load boards for the selected gym
      const boardsResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/boards/gym/${selectedGym.gymId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Boards response status:', boardsResponse.status);

      if (!boardsResponse.ok) {
        const errorData = await boardsResponse.json().catch(() => ({}));
        console.log('Boards error:', errorData);
        throw new Error(errorData.error || `Failed to load boards (HTTP ${boardsResponse.status})`);
      }

      const boardsData = await boardsResponse.json();
      console.log('Boards data:', boardsData);
      setBoards(boardsData);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickReserve = (board) => {
    setSelectedBoard(board);
    setShowQuickReserveModal(true);
  };

  const handleReservationComplete = (reservation) => {
    console.log('Reservation created:', reservation);
    // Store the reservation and show success modal
    setCompletedReservation(reservation);
    setShowSuccessModal(true);
    // Refresh upcoming reservations since we just made a new one
    setHasUpcomingReservations(null); // This will trigger a re-render of UpcomingReservations
    // Refresh board availability immediately after making a reservation
    loadDashboardData();
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setCompletedReservation(null);
  };

  const handleReservationsLoaded = (count) => {
    setHasUpcomingReservations(count > 0);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading your dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-coral-100 border-2 border-coral-500 shadow-retro">
            <svg className="h-8 w-8 text-coral-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-slate-900">
            Error loading dashboard
          </h2>
          <p className="mt-2 text-base text-slate-600">
            {error}
          </p>
          <div className="mt-6">
            <Button onClick={loadDashboardData}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-teal-600 via-purple-600 to-magenta-600 bg-clip-text text-transparent">
              {greeting}, {user.firstName}!
            </h1>
            <div className="mt-4">
              <GymSelectorTrigger onClick={() => setShowGymSelector(true)} />
            </div>
          </div>

          {/* Upcoming Reservations - only show if user has upcoming reservations */}
          {hasUpcomingReservations !== false && (
            <div className="mb-8">
              <UpcomingReservations onReservationsLoaded={handleReservationsLoaded} />
            </div>
          )}

          {/* Boards Grid */}
          <div className="mb-8">
            <div className="flex items-center mb-6">
              <div className="w-1 h-8 bg-gradient-to-b from-teal-500 to-purple-500 mr-3 rounded"></div>
              <h2 className="text-3xl font-bold text-slate-900">
                Available Boards
              </h2>
            </div>

            <BoardGrid
              boards={boards}
              onBoardSelect={handleQuickReserve}
            />
          </div>
        </div>
      </div>

      {/* Quick Reserve Modal */}
      <QuickReserveModal
        isOpen={showQuickReserveModal}
        onClose={() => setShowQuickReserveModal(false)}
        board={selectedBoard}
        onReservationComplete={handleReservationComplete}
      />

      {/* Reservation Success Modal */}
      <ReservationSuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        reservation={completedReservation}
      />

      {/* Gym Selector Modal */}
      <GymSelectorModal
        isOpen={showGymSelector}
        onClose={() => setShowGymSelector(false)}
      />
    </div>
  );
};

export default Dashboard;